/**
 * 用户体验优化统一入口
 * 整合PWA、国际化、加载优化等所有用户体验增强功能
 */

class UXOptimizationManager {
  constructor() {
    this.modules = new Map();
    this.config = {
      enablePWA: true,
      enableI18n: true,
      enableLoadingOptimization: true,
      enablePerformanceMonitoring: true,
      enableAccessibility: true,
      enableResponsiveDesign: true
    };

    // 不在构造函数中调用async方法,避免未捕获的Promise rejection
  }

  /**
   * 初始化UX优化管理器
   */
  async init() {
    console.log('[UX] Initializing UX Optimization Manager...');

    try {
      // 不显示全局加载状态，避免白色蒙版问题
      // this.showGlobalLoading();

      // 并行初始化所有模块
      const initPromises = [];

      if (this.config.enablePWA) {
        initPromises.push(this.initializePWA());
      }

      if (this.config.enableI18n) {
        initPromises.push(this.initializeI18n());
      }

      if (this.config.enableLoadingOptimization) {
        initPromises.push(this.initializeLoadingOptimization());
      }

      if (this.config.enablePerformanceMonitoring) {
        initPromises.push(this.initializePerformanceMonitoring());
      }

      if (this.config.enableAccessibility) {
        initPromises.push(this.initializeAccessibility());
      }

      if (this.config.enableResponsiveDesign) {
        initPromises.push(this.initializeResponsiveDesign());
      }

      // 等待所有模块初始化完成
      await Promise.all(initPromises);

      // 设置模块间通信
      this.setupInterModuleCommunication();

      // 初始化用户偏好
      this.initializeUserPreferences();

      // 不需要隐藏全局加载状态
      // this.hideGlobalLoading();

      console.log('[UX] UX Optimization Manager initialized successfully');

      // 触发初始化完成事件
      window.dispatchEvent(new CustomEvent('uxOptimizationReady', {
        detail: { modules: Array.from(this.modules.keys()) }
      }));

    } catch (error) {
      console.error('[UX] Failed to initialize UX Optimization Manager:', error);
      this.handleInitializationError(error);
    }
  }

  /**
   * 初始化PWA功能
   */
  async initializePWA() {
    try {
      if (window.PWAManager) {
        const pwa = new window.PWAManager();
        this.modules.set('pwa', pwa);
        console.log('[UX] PWA module initialized');
      } else {
        await this.loadScript('/js/modules/pwa/pwa-manager.js');
        const pwa = new window.PWAManager();
        this.modules.set('pwa', pwa);
      }
    } catch (error) {
      console.warn('[UX] PWA initialization failed:', error);
    }
  }

  /**
   * 初始化国际化
   * 复用i18n-manager.js中已经初始化好的window.i18n实例
   */
  async initializeI18n() {
    try {
      // 等待i18n初始化完成
      const waitForI18n = () => {
        return new Promise((resolve) => {
          // 如果已经存在并已初始化,直接使用
          if (window.i18n && window.i18n.translations && window.i18n.translations.size > 0) {
            resolve(window.i18n);
            return;
          }

          // 等待i18n初始化完成事件
          const handleI18nReady = () => {
            resolve(window.i18n);
            document.removeEventListener('i18nReady', handleI18nReady);
          };

          document.addEventListener('i18nReady', handleI18nReady);

          // 超时保护(5秒)
          setTimeout(() => {
            document.removeEventListener('i18nReady', handleI18nReady);
            resolve(window.i18n || null);
          }, 5000);
        });
      };

      const i18n = await waitForI18n();
      if (i18n) {
        this.modules.set('i18n', i18n);
        console.log('[UX] i18n module initialized (reused existing instance)');
      } else {
        console.warn('[UX] i18n instance not available after timeout');
      }
    } catch (error) {
      console.warn('[UX] i18n initialization failed:', error);
    }
  }

  /**
   * 初始化加载优化
   */
  async initializeLoadingOptimization() {
    try {
      if (window.LoadingOptimizationManager) {
        const loader = new window.LoadingOptimizationManager();
        this.modules.set('loader', loader);
        console.log('[UX] Loading optimization module initialized');
      } else {
        await this.loadScript('/js/modules/loading/loading-optimizer.js');
        const loader = new window.LoadingOptimizationManager();
        this.modules.set('loader', loader);
      }
    } catch (error) {
      console.warn('[UX] Loading optimization initialization failed:', error);
    }
  }

  /**
   * 初始化性能监控
   */
  async initializePerformanceMonitoring() {
    try {
      const performanceMonitor = {
        metrics: new Map(),
        observers: new Set(),

        // 监控核心Web指标
        observeWebVitals() {
          if ('PerformanceObserver' in window) {
            // 监控LCP
            const lcpObserver = new PerformanceObserver((list) => {
              const entries = list.getEntries();
              const lcp = entries[entries.length - 1];
              this.metrics.set('LCP', lcp.startTime);
              this.reportMetric('LCP', lcp.startTime);
            });

            try {
              lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
              this.observers.add(lcpObserver);
            } catch (e) {
              console.warn('[UX] LCP observer not supported');
            }

            // 监控FID
            const fidObserver = new PerformanceObserver((list) => {
              const entries = list.getEntries();
              entries.forEach(entry => {
                const fid = entry.processingStart - entry.startTime;
                this.metrics.set('FID', fid);
                this.reportMetric('FID', fid);
              });
            });

            try {
              fidObserver.observe({ entryTypes: ['first-input'] });
              this.observers.add(fidObserver);
            } catch (e) {
              console.warn('[UX] FID observer not supported');
            }

            // 监控CLS
            let cumulativeCLS = 0;
            const clsObserver = new PerformanceObserver((list) => {
              const entries = list.getEntries();
              entries.forEach(entry => {
                if (!entry.hadRecentInput) {
                  cumulativeCLS += entry.value;
                }
              });
              this.metrics.set('CLS', cumulativeCLS);
              this.reportMetric('CLS', cumulativeCLS);
            });

            try {
              clsObserver.observe({ entryTypes: ['layout-shift'] });
              this.observers.add(clsObserver);
            } catch (e) {
              console.warn('[UX] CLS observer not supported');
            }
          }
        },

        // 监控用户交互
        observeUserInteractions() {
          const interactionTypes = ['click', 'keydown', 'scroll', 'resize'];

          interactionTypes.forEach(type => {
            document.addEventListener(type, (event) => {
              const timestamp = performance.now();
              this.trackInteraction(type, timestamp, event);
            }, { passive: true });
          });
        },

        // 跟踪交互
        trackInteraction(type, timestamp, _event) {
          // Interaction data prepared for future analytics expansion
          // const interaction = {
          //   type,
          //   timestamp,
          //   target: event.target.tagName,
          //   x: event.clientX || 0,
          //   y: event.clientY || 0
          // };

          // 发送到分析服务
          if (window.gtag) {
            window.gtag('event', 'user_interaction', {
              interaction_type: type,
              timestamp
            });
          }
        },

        // 报告指标
        reportMetric(name, value) {
          console.log(`[UX] ${name}:`, value);

          // 发送到分析服务
          if (window.gtag) {
            window.gtag('event', 'web_vital', {
              metric_name: name,
              metric_value: Math.round(value)
            });
          }

          // 检查性能阈值
          this.checkPerformanceThresholds(name, value);
        },

        // 检查性能阈值
        checkPerformanceThresholds(name, value) {
          const thresholds = {
            'LCP': { good: 2500, poor: 4000 },
            'FID': { good: 100, poor: 300 },
            'CLS': { good: 0.1, poor: 0.25 }
          };

          const threshold = thresholds[name];
          if (!threshold) {return;}

          let rating = 'good';
          if (value > threshold.poor) {
            rating = 'poor';
          } else if (value > threshold.good) {
            rating = 'needs-improvement';
          }

          console.log(`[UX] ${name} rating:`, rating);

          // 如果性能差，触发优化建议
          if (rating === 'poor') {
            this.suggestOptimizations(name, value);
          }
        },

        // 建议优化
        suggestOptimizations(metric, value) {
          const suggestions = {
            'LCP': [
              '优化图片加载',
              '减少CSS阻塞时间',
              '优化服务器响应时间'
            ],
            'FID': [
              '减少JavaScript执行时间',
              '分割长任务',
              '优化事件处理器'
            ],
            'CLS': [
              '为图片添加尺寸属性',
              '避免动态注入内容',
              '预留广告空间'
            ]
          };

          console.warn(`[UX] ${metric} 性能较差 (${value}), 建议:`, suggestions[metric]);
        }
      };

      // 开始监控
      performanceMonitor.observeWebVitals();
      performanceMonitor.observeUserInteractions();

      this.modules.set('performance', performanceMonitor);
      console.log('[UX] Performance monitoring initialized');

    } catch (error) {
      console.warn('[UX] Performance monitoring initialization failed:', error);
    }
  }

  /**
   * 初始化无障碍功能
   */
  async initializeAccessibility() {
    try {
      const accessibility = {
        // 键盘导航增强
        enhanceKeyboardNavigation() {
          document.addEventListener('keydown', (event) => {
            // Tab键焦点管理
            if (event.key === 'Tab') {
              document.body.classList.add('keyboard-navigation');
            }

            // Escape键关闭模态框
            if (event.key === 'Escape') {
              const modal = document.querySelector('.modal:not([hidden])');
              if (modal) {
                modal.style.display = 'none';
                modal.setAttribute('hidden', '');
              }
            }
          });

          document.addEventListener('mousedown', () => {
            document.body.classList.remove('keyboard-navigation');
          });
        },

        // 跳转链接
        addSkipLink() {
          const skipLink = document.createElement('a');
          skipLink.href = '#main-content';
          skipLink.textContent = '跳转到主要内容';
          skipLink.className = 'skip-link';

          const style = document.createElement('style');
          style.textContent = `
            .skip-link {
              position: absolute;
              top: -40px;
              left: 6px;
              background: #000;
              color: #fff;
              padding: 8px;
              text-decoration: none;
              border-radius: 0 0 4px 4px;
              z-index: 10000;
              transition: top 0.3s;
            }
            .skip-link:focus {
              top: 0;
            }
          `;

          document.head.appendChild(style);
          document.body.insertAdjacentElement('afterbegin', skipLink);
        },

        // ARIA标签增强
        enhanceAriaLabels() {
          // 为按钮添加ARIA标签
          const buttons = document.querySelectorAll('button:not([aria-label])');
          buttons.forEach(button => {
            if (!button.textContent.trim() && !button.getAttribute('aria-label')) {
              button.setAttribute('aria-label', '按钮');
            }
          });

          // 为表单输入添加ARIA描述
          const inputs = document.querySelectorAll('input[required]:not([aria-required])');
          inputs.forEach(input => {
            input.setAttribute('aria-required', 'true');
          });
        },

        // 颜色对比度检查
        checkColorContrast() {
          // 这里可以实现颜色对比度检查逻辑
          console.log('[UX] Color contrast checking enabled');
        },

        // 屏幕阅读器支持
        enhanceScreenReaderSupport() {
          // 为动态内容添加aria-live区域
          const liveRegion = document.createElement('div');
          liveRegion.id = 'aria-live-region';
          liveRegion.setAttribute('aria-live', 'polite');
          liveRegion.setAttribute('aria-atomic', 'true');
          liveRegion.style.cssText = `
            position: absolute;
            left: -10000px;
            width: 1px;
            height: 1px;
            overflow: hidden;
          `;
          document.body.appendChild(liveRegion);

          // 通知屏幕阅读器
          window.announceToScreenReader = (message) => {
            liveRegion.textContent = message;
            setTimeout(() => {
              liveRegion.textContent = '';
            }, 1000);
          };
        }
      };

      // 初始化无障碍功能
      accessibility.enhanceKeyboardNavigation();
      accessibility.addSkipLink();
      accessibility.enhanceAriaLabels();
      accessibility.checkColorContrast();
      accessibility.enhanceScreenReaderSupport();

      this.modules.set('accessibility', accessibility);
      console.log('[UX] Accessibility features initialized');

    } catch (error) {
      console.warn('[UX] Accessibility initialization failed:', error);
    }
  }

  /**
   * 初始化响应式设计增强
   */
  async initializeResponsiveDesign() {
    try {
      const responsive = {
        breakpoints: {
          mobile: '(max-width: 767px)',
          tablet: '(min-width: 768px) and (max-width: 1023px)',
          desktop: '(min-width: 1024px)',
          largeDesktop: '(min-width: 1440px)'
        },

        currentBreakpoint: 'desktop',

        // 监听断点变化
        observeBreakpoints() {
          Object.entries(this.breakpoints).forEach(([name, query]) => {
            const mql = window.matchMedia(query);

            const handleBreakpointChange = (e) => {
              if (e.matches) {
                this.currentBreakpoint = name;
                document.body.setAttribute('data-breakpoint', name);

                // 触发断点变化事件
                window.dispatchEvent(new CustomEvent('breakpointchange', {
                  detail: { breakpoint: name, mediaQuery: query }
                }));

                console.log('[UX] Breakpoint changed to:', name);
              }
            };

            mql.addListener(handleBreakpointChange);
            handleBreakpointChange(mql); // 初始检查
          });
        },

        // 响应式图片管理
        manageResponsiveImages() {
          const images = document.querySelectorAll('img[data-responsive]');

          images.forEach(img => {
            const updateImageSource = () => {
              const sources = {
                mobile: img.dataset.mobileSrc,
                tablet: img.dataset.tabletSrc,
                desktop: img.dataset.desktopSrc
              };

              const currentSource = sources[this.currentBreakpoint] || sources.desktop;
              if (currentSource && img.src !== currentSource) {
                img.src = currentSource;
              }
            };

            // 监听断点变化
            window.addEventListener('breakpointchange', updateImageSource);
            updateImageSource(); // 初始设置
          });
        },

        // 响应式字体大小
        manageResponsiveFonts() {
          const root = document.documentElement;

          const fontSizes = {
            mobile: '14px',
            tablet: '15px',
            desktop: '16px',
            largeDesktop: '18px'
          };

          const updateFontSize = () => {
            root.style.fontSize = fontSizes[this.currentBreakpoint] || '16px';
          };

          window.addEventListener('breakpointchange', updateFontSize);
          updateFontSize();
        },

        // 触摸设备优化
        optimizeForTouch() {
          const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

          if (isTouchDevice) {
            document.body.classList.add('touch-device');

            // 增加按钮触摸区域
            const style = document.createElement('style');
            style.textContent = `
              .touch-device button,
              .touch-device .clickable {
                min-height: 44px;
                min-width: 44px;
                padding: 12px;
              }

              .touch-device .card {
                margin-bottom: 16px;
              }
            `;
            document.head.appendChild(style);
          }
        }
      };

      // 初始化响应式功能
      responsive.observeBreakpoints();
      responsive.manageResponsiveImages();
      responsive.manageResponsiveFonts();
      responsive.optimizeForTouch();

      this.modules.set('responsive', responsive);
      console.log('[UX] Responsive design features initialized');

    } catch (error) {
      console.warn('[UX] Responsive design initialization failed:', error);
    }
  }

  /**
   * 设置模块间通信
   */
  setupInterModuleCommunication() {
    // PWA 和 i18n 集成
    const pwa = this.modules.get('pwa');
    const i18n = this.modules.get('i18n');

    if (pwa && i18n) {
      i18n.addLanguageObserver((_event) => {
        console.log('[UX] Language changed, updating PWA manifest');
        // 可以在这里更新PWA的语言相关设置
      });
    }

    // 加载优化和性能监控集成
    const loader = this.modules.get('loader');
    const performance = this.modules.get('performance');

    if (loader && performance) {
      // 当图片加载完成时，通知性能监控
      window.addEventListener('imageLoaded', (event) => {
        performance.trackInteraction('image_loaded', performance.now(), event);
      });
    }

    console.log('[UX] Inter-module communication setup completed');
  }

  /**
   * 初始化用户偏好
   */
  initializeUserPreferences() {
    const savedPreferences = localStorage.getItem('ux-preferences');

    const defaultPreferences = {
      reduceMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
      darkMode: window.matchMedia('(prefers-color-scheme: dark)').matches,
      highContrast: window.matchMedia('(prefers-contrast: high)').matches,
      language: navigator.language.split('-')[0] || 'zh',
      notifications: true
    };

    const preferences = savedPreferences ?
      { ...defaultPreferences, ...JSON.parse(savedPreferences) } :
      defaultPreferences;

    // 应用用户偏好
    this.applyUserPreferences(preferences);

    // 监听系统偏好变化
    this.observeSystemPreferences();

    console.log('[UX] User preferences initialized:', preferences);
  }

  /**
   * 应用用户偏好
   */
  applyUserPreferences(preferences) {
    // 减少动画
    if (preferences.reduceMotion) {
      document.body.classList.add('reduce-motion');
    }

    // 暗色模式
    if (preferences.darkMode) {
      document.body.classList.add('dark-theme');
    }

    // 高对比度
    if (preferences.highContrast) {
      document.body.classList.add('high-contrast');
    }

    // 语言设置
    const i18n = this.modules.get('i18n');
    if (i18n && preferences.language !== i18n.currentLanguage) {
      i18n.switchLanguage(preferences.language);
    }
  }

  /**
   * 监听系统偏好变化
   */
  observeSystemPreferences() {
    // 监听减少动画偏好
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    motionQuery.addListener((e) => {
      document.body.classList.toggle('reduce-motion', e.matches);
    });

    // 监听色彩模式偏好
    const colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    colorSchemeQuery.addListener((e) => {
      document.body.classList.toggle('dark-theme', e.matches);
    });

    // 监听对比度偏好
    const contrastQuery = window.matchMedia('(prefers-contrast: high)');
    contrastQuery.addListener((e) => {
      document.body.classList.toggle('high-contrast', e.matches);
    });
  }

  /**
   * 显示全局加载状态
   */
  showGlobalLoading() {
    const loader = document.createElement('div');
    loader.id = 'global-ux-loader';
    loader.innerHTML = `
      <div class="ux-loader-content">
        <div class="ux-loader-spinner"></div>
        <p>正在优化用户体验...</p>
      </div>
    `;

    const style = document.createElement('style');
    style.textContent = `
      #global-ux-loader {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.85);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        backdrop-filter: blur(4px);
      }

      .ux-loader-content {
        text-align: center;
        color: #ffffff;
      }

      .ux-loader-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid #f3f3f3;
        border-top: 3px solid #53d22c;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 16px;
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(loader);
  }

  /**
   * 隐藏全局加载状态
   */
  hideGlobalLoading() {
    const loader = document.getElementById('global-ux-loader');
    if (loader) {
      loader.style.opacity = '0';
      setTimeout(() => {
        loader.remove();
      }, 300);
    }
  }

  /**
   * 处理初始化错误
   */
  handleInitializationError(error) {
    console.error('[UX] Initialization error:', error);

    // 不需要隐藏加载状态，因为没有显示
    // this.hideGlobalLoading();

    // 使用增强的错误处理系统
    if (window.errorHandler) {
      const appError = window.errorHandler.createAppError({
        name: 'UXInitializationError',
        message: error.message || '用户体验优化初始化失败',
        code: 'UX_INIT_FAILED',
        severity: 'medium',
        context: {
          modules: Array.from(this.modules.keys()),
          config: this.config,
        },
        userFriendly: true,
        retryable: true,
      });

      window.errorHandler.handleError(appError, {
        userFriendlyMessage: '用户体验功能初始化失败，但应用仍可正常使用。',
        showUserNotification: true,
      });
    } else {
      // 降级处理
      this.showErrorNotification('用户体验优化功能初始化失败，但应用仍可正常使用。');
    }
  }

  /**
   * 显示错误通知
   */
  showErrorNotification(message) {
    const notification = document.createElement('div');
    notification.className = 'ux-error-notification';
    notification.textContent = message;

    const style = document.createElement('style');
    style.textContent = `
      .ux-error-notification {
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ff5722;
        color: white;
        padding: 16px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        z-index: 10001;
        max-width: 350px;
        animation: slideIn 0.3s ease-out;
      }

      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(notification);

    // 5秒后自动移除
    setTimeout(() => {
      notification.remove();
    }, 5000);
  }

  /**
   * 动态加载脚本
   */
  loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * 获取模块状态
   */
  getModuleStatus() {
    const status = {};
    for (const [name, module] of this.modules) {
      status[name] = {
        loaded: true,
        initialized: !!module,
        hasError: false
      };
    }
    return status;
  }

  /**
   * 获取UX指标
   */
  getUXMetrics() {
    const performance = this.modules.get('performance');
    const loader = this.modules.get('loader');

    return {
      performance: performance ? performance.metrics : new Map(),
      loading: loader ? loader.getPerformanceMetrics() : {},
      modules: this.getModuleStatus(),
      timestamp: Date.now()
    };
  }
}

// 导出UX优化管理器
window.UXOptimizationManager = UXOptimizationManager;

// 等待i18n初始化完成后再自动初始化UXOptimizationManager
document.addEventListener('DOMContentLoaded', async () => {
  try {
    // 等待i18n初始化完成
    await new Promise((resolve) => {
      if (window.i18n && window.i18n.translations && window.i18n.translations.size > 0) {
        resolve();
        return;
      }

      const handleI18nReady = () => {
        resolve();
        document.removeEventListener('i18nReady', handleI18nReady);
      };

      document.addEventListener('i18nReady', handleI18nReady);

      // 超时保护(3秒后继续初始化)
      setTimeout(resolve, 3000);
    });

    // 创建并初始化UXOptimizationManager
    window.uxOptimizer = new UXOptimizationManager();
    await window.uxOptimizer.init();

  } catch (error) {
    console.error('[UX] Failed to initialize UX Optimization Manager:', error);
  }
});